<!--
  - Copyright (C) 2021. Archimedes Exhibitions GmbH,
  - Saarbrücker Str. 24, Berlin, Germany
  -
  - This file contains proprietary source code and confidential
  - information. Its contents may not be disclosed or distributed to
  - third parties unless prior specific permission by Archimedes
  - Exhibitions GmbH, Berlin, Germany is obtained in writing. This applies
  - to copies made in any form and using any medium. It applies to
  - partial as well as complete copies.
  -->

<template>
  <div>
    <b-modal
      id="contentToPick"
      scrollable
      size="xl"
      :title="isSync ? $t('transfer.pickSync') : $t('transfer.pickPublish')"
      :hide-footer="true"
    >
      <div class="search-wrapper">
        <input
          type="text"
          ref="contentSearch"
          v-model="contentSearch"
          :placeholder="$t('ems.common.search')"
        />
      </div>

      <b-table
        hover
        head-variant="light"
        class="mt-5 row-pointer"
        ref="table"
        :fields="contentFields"
        :items="pickedContents"
        :filter="contentSearch"
        @row-clicked="onContentPicked"
      >
        <template v-slot:cell(image)="row">
          <div>
            <b-img
              v-if="row.item.thumbNail"
              rounded
              :src="row.item.thumbNail"
              :alt="getTruncatedString(row.item.displayName)"
              :height="thumbSize"
              :width="thumbSize"
              :id="`popover-${row.item.uuid}`"
            >
            </b-img>
          </div>
        </template>

        <template v-slot:cell(displayName)="row">
          {{ getTruncatedString(row.item.displayName) }}
        </template>

        <template v-slot:cell(created)="row">
          <small>{{ formatDate(row.item.created) }}</small>
        </template>

        <template v-slot:cell(creator)="row">
          <span class="text-secondary" v-if="row.item.creator">
            {{ row.item.creator.username }}
          </span>
        </template>

        <template v-slot:cell(contentVersion)="row">
          <b-badge variant="primary">
            {{ row.item.contentVersion }}
          </b-badge>
        </template>
      </b-table>
    </b-modal>

    <b-modal
      id="jobHistory"
      scrollable
      size="xl"
      :title="$t('transfer.jobHistory')"
      :hide-footer="true"
    >
      <job-history :job-history="jobHistory" :driver="driver"></job-history>
    </b-modal>

    <!-- Secondary navigation-->
    <b-navbar toggleable="sm" type="light" variant="light">
      <b-navbar-nav class="ml-auto">
        <b-nav-item>
          <b-button
            :disabled="!userHasAccess([scopeMethods.WRITE], false)"
            class="show-sidebar-button"
            variant="primary"
            @click="syncExhibits()"
          >
            <b-icon-broadcast-pin></b-icon-broadcast-pin>
            {{ $t('transfer.syncExhibits') }}
          </b-button>
        </b-nav-item>
      </b-navbar-nav>
    </b-navbar>

    <b-container v-if="isSetup" class="py-4" fluid>
      <div class="search-wrapper">
        <input
          type="text"
          ref="search"
          v-model="searchQuery"
          :placeholder="$t('ems.common.search')"
        />
      </div>

      <b-table
        hover
        head-variant="light"
        class="mt-5 row-pointer"
        ref="table"
        :fields="exhibitFields"
        :items="resultQuery"
      >
        <template v-slot:cell(online)="row">
          <div v-if="exhibitHealth[row.item.cproxy]">
            <b
              v-if="exhibitHealth[row.item.cproxy] === 'HEALTH_STATUS_SERVING'"
              class="text-success"
            >
              {{ $t('transfer.clientHealthServing') }}
            </b>
            <b
              v-else-if="
                exhibitHealth[row.item.cproxy] === 'HEALTH_STATUS_NOT_SERVING'
              "
              class="text-warning"
            >
              {{ $t('transfer.clientHealthNotServing') }}
            </b>
            <b v-else class="text-secondary">{{
              $t('transfer.clientStatusUnknown')
            }}</b>
          </div>
          <div v-else>
            <b>{{ $t('transfer.clientStatusUnknown') }}</b>
          </div>
        </template>

        <template v-slot:cell(lastPull)="row">
          <div
            v-if="
              exhibitInfo[row.item.exhibit] &&
              exhibitInfo[row.item.exhibit].pullTime
            "
          >
            {{ getDateFromGrpc(exhibitInfo[row.item.exhibit].pullTime) }}
          </div>
        </template>

        <template v-slot:cell(publishStatus)="row">
          <div
            v-if="
              exhibitInfo[row.item.exhibit] &&
              exhibitInfo[row.item.exhibit].contentInfo
            "
          >
            <div
              class="d-inline"
              v-if="
                getContentFromExhibit(
                  row.item,
                  exhibitInfo[row.item.exhibit].contentInfo.contentId
                )
              "
            >
              <b-img
                class="mr-1"
                v-if="
                  getContentFromExhibit(
                    row.item,
                    exhibitInfo[row.item.exhibit].contentInfo.contentId
                  ).thumbNail
                "
                rounded
                :src="
                  getContentFromExhibit(
                    row.item,
                    exhibitInfo[row.item.exhibit].contentInfo.contentId
                  ).thumbNail
                "
                :height="thumbSize"
                :width="thumbSize"
                :id="`popover-${row.item.cproxy}${row.item.exhibit}`"
              >
              </b-img>
              <b-popover
                placement="right"
                v-if="
                  getContentFromExhibit(
                    row.item,
                    exhibitInfo[row.item.exhibit].contentInfo.contentId
                  ).thumbNail
                "
                :target="`popover-${row.item.cproxy}${row.item.exhibit}`"
                triggers="hover"
              >
                <b-img
                  fluid
                  :src="
                    getContentFromExhibit(
                      row.item,
                      exhibitInfo[row.item.exhibit].contentInfo.contentId
                    ).preview
                  "
                ></b-img>
              </b-popover>
              {{
                getTruncatedString(
                  getContentFromExhibit(
                    row.item,
                    exhibitInfo[row.item.exhibit].contentInfo.contentId
                  ).displayName,
                  30
                )
              }}
            </div>
            <div v-else class="d-inline">
              {{ exhibitInfo[row.item.exhibit].contentInfo.contentId }}
            </div>
          </div>
        </template>

        <template v-slot:cell(contentVersion)="row">
          <b-badge
            variant="primary"
            v-if="
              exhibitInfo[row.item.exhibit] &&
              exhibitInfo[row.item.exhibit].contentInfo
            "
          >
            {{ exhibitInfo[row.item.exhibit].contentInfo.version }}
          </b-badge>
        </template>

        <template v-slot:cell(syncStatus)="row">
          <b class="text-primary" v-if="hasQueuedJobs(row.item.cproxy)">{{
            $t('transfer.contentStatusQueued')
          }}</b>
          <b class="text-warning" v-else-if="hasSyncingJobs(row.item.cproxy)">{{
            $t('transfer.contentStatusSync')
          }}</b>
          <b
            class="text-success"
            v-else-if="getFinishedJobs(row.item.cproxy).length"
            >{{ $t('transfer.contentStatusReady') }}</b
          >
        </template>

        <template v-slot:cell(actions)="row">
          <b-button
            :disabled="
              !row.item.exhibit ||
              row.item.contents.length == 0 ||
              !userHasAccess([scopeMethods.WRITE], false)
            "
            class="mb-1 ml-1"
            variant="dark"
            size="sm"
            @click="syncExhibit(row.item)"
          >
            <b-icon-arrow-repeat></b-icon-arrow-repeat>
            {{ $t('transfer.btnPull') }}
          </b-button>

          <b-button
            :disabled="
              !row.item.exhibit ||
              row.item.contents.length == 0 ||
              !userHasAccess([scopeMethods.WRITE], false)
            "
            class="mb-1 ml-1"
            variant="primary"
            size="sm"
            @click="pickContentsToPublish(row.item)"
          >
            <b-icon-cast></b-icon-cast>
            {{ $t('transfer.btnPush') }}
          </b-button>

          <b-button
            :disabled="!row.item.exhibit || row.item.contents.length == 0"
            class="mb-1 ml-1"
            variant="outline-dark"
            size="sm"
            @click="loadJobHistory(row.item)"
          >
            <b-icon-clock-history></b-icon-clock-history>
          </b-button>
        </template>
      </b-table>
      <b-alert show v-if="!resultQuery.length" class="text-center">
        <b-icon-exclamation-diamond></b-icon-exclamation-diamond>
        {{ $t('ems.common.nothingFound') }}
      </b-alert>
      <b-alert
        v-model="isLoading"
        class="position-fixed fixed-bottom m-0 rounded-0 text-center"
        style="z-index: 2000"
        variant="primary"
      >
        <b-icon-alarm></b-icon-alarm>
        {{ $t('cms.common.loadingWait') }}
      </b-alert>
      <div class="text-center">
        <small>{{ pkginfo.name }} v{{ pkginfo.version }}</small>
      </div>
    </b-container>
  </div>
</template>

<script>
  import Vue from 'vue'
  import { BootstrapVue, IconsPlugin } from 'bootstrap-vue'
  import { utils as libUtils } from '@amdx/emslib'

  import 'bootstrap-vue/dist/bootstrap-vue.css'
  import 'bootstrap/dist/css/bootstrap.css'

  import './scss/_globals.scss'

  import packageJson from '../package'
  import JobHistory from './components/JobHistory'

  Vue.use(BootstrapVue)
  Vue.use(IconsPlugin)

  export default {
    name: 'transfer',
    components: { JobHistory },

    props: {
      thumbSize: {
        default: 40
      }
    },

    data () {
      return {
        driver: null,
        isSetup: false,
        isLoading: true,
        searchQuery: null,
        exhibitFields: [
          {
            key: 'exhibit',
            label: this.$t('transfer.exhibit'),
            sortable: true,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'cproxy',
            label: this.$t('transfer.cproxy'),
            sortable: true,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'online',
            label: this.$t('transfer.status'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'syncStatus',
            label: this.$t('transfer.syncStatus'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'contentVersion',
            label: this.$t('transfer.contentVersion'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'publishStatus',
            label: this.$t('transfer.publishStatus'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'actions',
            label: 'Actions',
            sortable: false,
            thClass: 'text-right',
            tdClass: 'asset-text-cell align-middle text-right'
          }
        ],
        exhibitList: [],
        contentFields: [
          {
            key: 'image',
            label: this.$t('cms.common.image'),
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell'
          },
          {
            key: 'displayName',
            label: this.$t('cms.common.title'),
            sortable: true,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell'
          },
          {
            key: 'contentVersion',
            label: this.$t('transfer.contentVersion'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell'
          }
        ],
        pickedExhibit: null,
        pickedContents: null,
        contentSearch: '',
        isSync: false,
        syncingJobs: {}, // by jobId
        queuedJobs: {}, // by jobId
        finishedJobs: [],
        exhibitInfo: {},
        exhibitHealth: {},
        notListedContent: {},
        jobHistory: []
      }
    },

    computed: {
      pkginfo () {
        return packageJson
      },

      resultQuery () {
        if (this.searchQuery) {
          return this.exhibitList.filter((item) => {
            return this.searchQuery
              .toLowerCase()
              .split(' ')
              .every(
                (v) =>
                  item.exhibit.toLowerCase().includes(v) ||
                  item.cproxy.toLowerCase().includes(v)
              )
          })
        } else {
          return this.exhibitList
        }
      },

      scopeMethods () {
        return this.$keycloakmanager.scopeMethods
      }
    },

    methods: {
      loadData () {
        this.isLoading = true
        this.$driverManager
          .updateBrokerData()
          .then(async () => {
            this.exhibitInfo = {}
            this.exhibitList = []
            this.syncingJobs = {}
            this.queuedJobs = {}
            this.finishedJobs = []

            this.driver = this.$driverManager.drivers.find(
              (d) => d.name === 'CProxyDriver'
            )
            const proxies = this.$driverManager.clients.filter(
              (c) => c.driverId === this.driver.driverId
            )

            for (const proxy of proxies) {
              await this.setupProxy(proxy)
            }
            await this.checkForMissingCMSData()

            this.isLoading = false
          })
          .catch((error) => {
            libUtils.toast(this, 'Loading broker data failed!', error)
            this.isLoading = false
          })
      },

      async setupProxy (proxy) {
        try {
          await this.setupClientData(this.driver, proxy)
          const jobs = await this.getJobs(this.driver, proxy)
          for (const job of jobs) {
            this.onJobStreamData(proxy.clientId, job)
          }
        } catch (error) {
          console.error('Grabbing clients failed!', error)
          const exhibitData = {
            exhibit: null,
            cproxy: proxy.clientId,
            contents: []
          }
          if (!this.isExistingExhibit(exhibitData)) {
            this.exhibitList.push(exhibitData)
          }
        }
      },

      async loadCMSData (clientNames, proxy) {
        const cmsClients = await this.getClientsFromCMS(clientNames)

        if (cmsClients === null) {
          for (let name of clientNames) {
            const title = 'No content for ' + name + '!'
            const desc =
              'Could not find a client named ' +
              name +
              ' in the cms. Please add one!'
            libUtils.toast(this, title, desc)
            console.error(title + ' ' + desc)
            const exhibitData = {
              exhibit: name,
              cproxy: proxy.clientId,
              contents: []
            }
            if (!this.isExistingExhibit(exhibitData)) {
              this.exhibitList.push(exhibitData)
            }
          }
        } else {
          for (const cmsClient of cmsClients) {
            let fieldName = null
            const schema = this.$cmsManager.getSchemaWithName(
              cmsClient.schema.name
            )

            for (const field of schema.fields) {
              if (field.type === 'list' && field.listType === 'node') {
                fieldName = field.name
              }
            }

            if (fieldName === null) {
              const title = cmsClient.displayName + ' is not shown!'
              const desc =
                schema.name +
                ' has no node list field and can' +
                ' not be processed by this plugin!'
              libUtils.toast(this, title, desc)
              console.error(title + ' ' + desc)
              continue
            }

            for (const content of cmsClient.fields[fieldName]) {
              content.thumbNail = this.getPreview(
                content,
                this.thumbSize,
                this.thumbSize
              )
              content.preview = this.getPreview(content, 300, null, false)
            }
            const exhibitData = {
              exhibit: cmsClient.displayName,
              cproxy: proxy.clientId,
              contents: cmsClient.fields[fieldName]
            }
            if (!this.isExistingExhibit(exhibitData)) {
              this.exhibitList.push(exhibitData)
            }
          }
        }
      },

      async setupClientData (driver, proxy) {
        let isNext = true
        let nextToken = null
        let clientNames = []
        while (isNext) {
          const data = await this.$driverManager.sendAction(
            'ListClients',
            proxy,
            driver,
            { pageToken: nextToken }
          )
          if (data.nextPageToken) {
            nextToken = data.nextPageToken
          } else {
            isNext = false
          }

          for (const clientInfo of data.clientsList) {
            this.$set(this.exhibitInfo, clientInfo.clientId, clientInfo)
            clientNames.push(clientInfo.clientId)
          }
        }

        await this.loadCMSData(clientNames, proxy)
      },

      async getClientsFromCMS (clientNames) {
        const container = this.$cmsManager.containers[0]
        const uuid = this.$cmsManager.getContainerUUID(container.name)
        const schemas = this.$cmsManager.getContainerSchemas(uuid)
        let data = await this.$cmsManager.getNodesChildren(
          uuid,
          this.$cmsManager.defaultLanguage,
          'published'
        )
        let uuids = []
        for (const node of data.data) {
          const displayName = clientNames.find((n) => n === node.displayName)
          if (displayName) {
            uuids.push(node.uuid)
          }
        }
        if (!uuids.length) {
          return null
        }
        data = await this.$cmsManager.getCompleteNodesByUuid(
          uuids,
          this.$cmsManager.defaultLanguage,
          schemas,
          'published'
        )
        return data.data.nodes.elements
      },

      async getJobs (driver, proxy) {
        let isNext = true
        let nextToken = null
        const jobs = []
        while (isNext) {
          const data = await this.$driverManager.sendAction(
            'ListJobs',
            proxy,
            driver,
            { pageToken: nextToken }
          )
          if (data) {
            for (const job of data.jobsList) {
              jobs.push(job)
            }
            if (data.nextPageToken) {
              nextToken = data.nextPageToken
            } else {
              isNext = false
            }
          } else {
            isNext = false
          }
        }
        return jobs
      },

      async checkForMissingCMSData () {
        let exhibitList = JSON.parse(JSON.stringify(this.exhibitList))
        let uuids = []

        for (let clientName in this.exhibitInfo) {
          for (let data of exhibitList) {
            if (
              clientName === data.exhibit &&
              this.exhibitInfo[clientName].contentInfo
            ) {
              const contentId =
                this.exhibitInfo[clientName].contentInfo.contentId
              const cmsData = data.contents.find((c) => c.uuid === contentId)
              if (!cmsData) {
                uuids.push(contentId)
              }
            }
          }
        }

        if (uuids.length) {
          const container = this.$cmsManager.containers[1]
          const uuid = this.$cmsManager.getContainerUUID(container.name)
          const schemas = this.$cmsManager.getContainerSchemas(uuid)
          const data = await this.$cmsManager.getCompleteNodesByUuid(
            uuids,
            this.$cmsManager.defaultLanguage,
            schemas
          )
          for (const node of data.data.nodes.elements) {
            node.thumbNail = this.getPreview(
              node,
              this.thumbSize,
              this.thumbSize
            )
            node.preview = this.getPreview(node, 300, null, false)
            this.notListedContent[node.uuid] = node
          }
        }
      },

      syncExhibits () {
        for (const exhibit of this.exhibitList) {
          this.syncExhibit(exhibit)
        }
      },

      syncExhibit (exhibit) {
        for (const content of exhibit.contents) {
          this.syncContent(exhibit.cproxy, content)
        }
      },

      syncContent (proxyId, content) {
        const proxy = this.$driverManager.clients.find(
          (c) => c.clientId === proxyId
        )
        this.$driverManager
          .sendAction('Sync', proxy, this.driver, { contentId: content.uuid })
          .then((job) => {
            this.onJobStreamData(proxyId, job)
          })
          .catch((error) => {
            libUtils.toast(this, 'Syncing ' + proxyId + ' failed!', error)
          })
      },

      async pickContentsToPublish (exhibit) {
        try {
          this.isSync = false
          this.isLoading = true
          this.contentSearch = ''
          this.pickedExhibit = exhibit
          let uuids = []
          let versions = {}
          let isNext = true
          let nextToken = null

          const proxy = this.$driverManager.clients.find(
            (c) => c.clientId === exhibit.cproxy
          )
          while (isNext) {
            const data = await this.$driverManager.sendAction(
              'List',
              proxy,
              this.driver,
              { pageToken: nextToken }
            )
            if (data) {
              for (const contentData of data.contentInfosList) {
                uuids.push(contentData.contentId)
                versions[contentData.contentId] = contentData.version
              }
              if (data.nextPageToken) {
                nextToken = data.nextPageToken
              } else {
                isNext = false
              }
            } else {
              isNext = false
            }
          }

          let contents = []
          for (const content of exhibit.contents) {
            if (uuids.includes(content.uuid)) {
              content.thumbNail = this.getPreview(
                content,
                this.thumbSize,
                this.thumbSize
              )
              content.preview = this.getPreview(content, 300, null, false)
              content.contentVersion = versions[content.uuid]
              contents.push(content)
            }
          }
          this.pickedContents = contents
          this.$bvModal.show('contentToPick')
        } catch (error) {
          libUtils.toast(this, 'Loading synced contents failed!', error)
        }
        this.isLoading = false
      },

      async loadJobHistory (exhibit) {
        this.jobHistory = []
        this.$bvModal.show('jobHistory')
        const proxy = this.$driverManager.clients.find(
          (c) => c.clientId === exhibit.cproxy
        )
        try {
          const jobs = await this.getJobs(this.driver, proxy)
          for (const job of jobs) {
            const content = this.getContentFromExhibit(exhibit, job.contentId)
            if (content) {
              job.content = {
                title: this.getTruncatedString(content.displayName, 30),
                thumbNail: content.thumbNail,
                preview: content.preview
              }
              job.updated = this.getDateFromGrpc(job.updated)
              job.created = this.getDateFromGrpc(job.created)
              this.jobHistory.push(job)
            }
          }
        } catch (error) {
          libUtils.toast(
            this,
            'Load job history ' + exhibit.cproxy + ' failed!',
            error
          )
        }
      },

      formatDate (dateStr, includeTime = true) {
        if (dateStr) {
          let date = new Date(dateStr)
          let date_ =
            date.getDate() +
            '.' +
            (date.getMonth() + 1) +
            '.' +
            date.getFullYear()
          if (includeTime) {
            date_ +=
              ' ' +
              (date.getHours() < 10 ? '0' : '') +
              date.getHours() +
              ':' +
              (date.getMinutes() < 10 ? '0' : '') +
              date.getMinutes() +
              ':' +
              (date.getSeconds() < 10 ? '0' : '') +
              date.getSeconds()
          }
          return date_
        }
        return null
      },

      getDateFromGrpc (dateData) {
        const date = new Date(dateData.seconds * 1000)
        let date_ =
          date.getDate() +
          '.' +
          (date.getMonth() + 1) +
          '.' +
          date.getFullYear()
        date_ +=
          ' ' +
          (date.getHours() < 10 ? '0' : '') +
          date.getHours() +
          ':' +
          (date.getMinutes() < 10 ? '0' : '') +
          date.getMinutes() +
          ':' +
          (date.getSeconds() < 10 ? '0' : '') +
          date.getSeconds()
        return date_
      },

      getContentFromExhibit (exhibit, contentId) {
        let content = exhibit.contents.find((t) => t.uuid === contentId)
        if (content) {
          return content
        }
        content = this.notListedContent[contentId]
        if (content) {
          return content
        }
        return null
      },

      getTruncatedString (string, maxLength = 50) {
        return libUtils.getTruncatedString(string, maxLength)
      },

      getBinaryURL (node, fieldName, crop = null, w = null, h = null) {
        let field = node.fields[fieldName]
        let url = null
        if (field.mimeType.includes('image')) {
          let query = '?lang=' + node.language + '&version=' + node.version
          if (crop) {
            query += '&crop=fp'
          } else {
            query += '&resize=smart'
          }
          if (w) {
            query += '&w=' + w
          }
          if (h) {
            query += '&h=' + h
          } else if (w && crop) {
            query += '&h=' + w
          }
          url =
            this.$cmsManager.projectUrl +
            '/nodes' +
            '/' +
            node.uuid +
            '/binary/' +
            fieldName +
            query
        } else if (
          field.mimeType.includes('video') ||
          field.mimeType.includes('audio')
        ) {
          let query = '?lang=' + node.language + '&version=' + node.version
          url =
            this.$cmsManager.projectUrl +
            '/nodes' +
            '/' +
            node.uuid +
            '/binary/' +
            fieldName +
            query
        }
        return url
      },

      getPreview (node, w = null, h = null, crop = true) {
        if (!node.schema) {
          return null
        }

        let schema = this.$cmsManager.getSchemaWithName(node.schema.name)

        let binaryFields = schema.fields.filter((f) => f.type === 'binary')
        if (binaryFields && binaryFields.length) {
          for (let binaryField of binaryFields) {
            let field = node.fields[binaryField.name]
            if (field && field.mimeType && field.mimeType.includes('image')) {
              let url = this.getBinaryURL(node, binaryField.name, crop, w, h)
              return url
            }
          }
        }

        let nodeFields = schema.fields.filter((f) => f.type === 'node')
        if (nodeFields && nodeFields.length) {
          for (let nodeField of nodeFields) {
            let field = node.fields[nodeField.name]
            if (field) {
              return this.getPreview(field, w, h, crop)
            }
          }
        }

        let nodeListFields = schema.fields.filter(
          (f) => f.type === 'list' && f.listType === 'node'
        )
        if (nodeListFields && nodeListFields.length) {
          for (let nodeListField of nodeListFields) {
            let nodes = node.fields[nodeListField.name]
            if (nodes) {
              for (let n of nodes) {
                let thumb = this.getPreview(n, w, h, crop)
                if (thumb) {
                  return thumb
                }
              }
            }
          }
        }

        return null
      },

      isExistingExhibit (exhibit) {
        return !!this.exhibitList.find(
          (e) => e.exhibit === exhibit.exhibit && e.cproxy === exhibit.cproxy
        )
      },

      onContentPicked (content) {
        this.$bvModal.hide('contentToPick')
        if (this.isSync) {
          this.syncContent(this.pickedExhibit.cproxy, content)
        } else {
          const proxy = this.$driverManager.clients.find(
            (c) => c.clientId === this.pickedExhibit.cproxy
          )
          this.$driverManager
            .sendAction('Publish', proxy, this.driver, {
              contentId: content.uuid,
              clientIdsList: [this.pickedExhibit.exhibit]
            })
            .then(async (clientInfos) => {
              for (const clientInfo of clientInfos.clientInfosList) {
                this.$set(this.exhibitInfo, clientInfo.clientId, clientInfo)
                await this.checkForMissingCMSData()
              }
            })
            .catch((error) => {
              libUtils.toast(
                this,
                'Publishing ' + this.pickedExhibit.exhibit + ' failed!',
                error
              )
            })
        }
      },

      onJobStreamData (proxyId, job) {
        job.cproxy = proxyId
        job.hasError = false

        const existingJob = this.finishedJobs.find(
          (j) => j.cproxy === proxyId && j.contentId === job.contentId
        )
        if (existingJob) {
          const jobIndex = this.finishedJobs.indexOf(existingJob)
          this.$delete(this.finishedJobs, jobIndex)
        }

        const driver = this.driver
        const statusString = driver.getStatusString(job.status)
        if (statusString === 'STATUS_QUEUED') {
          this.$set(this.queuedJobs, job.jobId, job)
        } else if (statusString === 'STATUS_SYNCING') {
          if (this.queuedJobs[job.jobId] !== undefined) {
            this.$delete(this.queuedJobs, job.jobId)
          }
          this.$set(this.syncingJobs, job.jobId, job)
        } else if (statusString === 'STATUS_ERROR') {
          if (this.queuedJobs[job.jobId] !== undefined) {
            this.$delete(this.queuedJobs, job.jobId)
          }
          if (this.syncingJobs[job.jobId] !== undefined) {
            this.$delete(this.syncingJobs, job.jobId)
          }
          job.hasError = true
          this.$set(this.finishedJobs, this.finishedJobs.length, job)
        } else if (statusString === 'STATUS_COMPLETED') {
          if (this.queuedJobs[job.jobId] !== undefined) {
            this.$delete(this.queuedJobs, job.jobId)
          }
          if (this.syncingJobs[job.jobId] !== undefined) {
            this.$delete(this.syncingJobs, job.jobId)
          }
          this.$set(this.finishedJobs, this.finishedJobs.length, job)
        }
      },

      getFinishedJobs (proxyId) {
        return this.finishedJobs.filter((j) => j.cproxy === proxyId)
      },

      hasSyncingJobs (proxyId) {
        for (const key in this.syncingJobs) {
          const job = this.syncingJobs[key]
          if (job.cproxy === proxyId) {
            return true
          }
        }
        return false
      },

      hasQueuedJobs (proxyId) {
        for (const key in this.queuedJobs) {
          const job = this.queuedJobs[key]
          if (job.cproxy === proxyId) {
            return true
          }
        }
        return false
      },

      async onClientsStreamData (proxyId, clientInfo) {
        this.$set(this.exhibitInfo, clientInfo.clientId, clientInfo)
        await this.checkForMissingCMSData()
      },

      async onDriverSubscribeUpdate (data) {
        if (data.status.isHealthStatus) {
          if (data.status.uiStatus === 'HEALTH_STATUS_SERVING') {
            const exhibit = this.exhibitList.find(
              (e) => e.cproxy === data.clientId && !e.exhibit
            )
            if (exhibit) {
              const i = this.exhibitList.indexOf(exhibit)
              this.$delete(this.exhibitList, i)
              const proxy = this.$driverManager.clients.find(
                (c) => c.clientId === data.clientId
              )
              await this.setupProxy(proxy)
            }
          }
          this.$set(this.exhibitHealth, data.clientId, data.status.uiStatus)
        } else if (data.status.job) {
          this.onJobStreamData(data.clientId, data.status.job)
        } else if (data.status.clientInfo) {
          this.onClientsStreamData(data.clientId, data.status.clientInfo)
        }
      },

      userHasAccess (methods, isPermissive = true) {
        return this.$keycloakmanager.userHasAccess(
          this.$keycloakmanager.scopes.TRANSFER,
          methods,
          isPermissive
        )
      }
    },

    created () {
      this.$driverManager.$on('client-update', this.onDriverSubscribeUpdate)
      if (!this.$cmsManager.isSetup) {
        this.isLoading = true
        this.$cmsManager
          .setup()
          .then(() => {
            this.isSetup = true
            this.loadData()
          })
          .catch((error) => {
            libUtils.toast(this, 'CMS setup failed', error)
            this.isLoading = false
          })
      } else {
        this.isSetup = true
        this.loadData()
      }
    },

    beforeDestroy () {
      this.$driverManager.$off('client-update', this.onDriverSubscribeUpdate)
    }
  }
</script>

<style scoped>
  .version-tag {
    color: #000;
    font-size: 12px;
  }
</style>
